import { Box, styled, Tab, Tabs } from '@mui/material';

import type { TabVariant } from '@shared/ui-kit/tabs/NavigationTabs/types';

type TabProps = {
  minWidthTabs?: string;
  isLastTabOnTheRight?: boolean;
  hideTabsLabel?: boolean;
  tabVariant?: TabVariant;
  hasLabel?: boolean;
  isSmallSize?: boolean;
  isHideTab?: boolean;
};
export const TabContainer = styled(Tab, {
  shouldForwardProp: (prop: string) =>
    ![
      'minWidthTabs',
      'isLastTabOnTheRight',
      'hideTabsLabel',
      'tabVariant',
      'hasLabel',
      'isSmallSize',
      'isHideTab',
    ].includes(prop),
})<TabProps>(
  ({
    minWidthTabs,
    theme,
    isLastTabOnTheRight,
    hideTabsLabel,
    tabVariant,
    hasLabel,
    isSmallSize,
    isHideTab,
  }) => ({
    ...(isHideTab && {
      display: 'none',
    }),

    minWidth: minWidthTabs ?? 'max-content',
    marginLeft: !hideTabsLabel && isLastTabOnTheRight ? 'auto' : 'unset',
    maxHeight: isSmallSize ? '24px' : 'initial',
    minHeight: isSmallSize ? '24px' : 'initial',
    maxWidth: '100%',
    position: 'relative',
    ...(tabVariant === 'green' && {
      padding: '8px 4px',
      '&.Mui-selected': {
        svg: { path: { fill: theme.palette.case.aquamarine.a700 } },
        color: theme.palette.case.aquamarine.a700,
      },
    }),

    ...(tabVariant === 'dark' && {
      transition: 'background-color 0.4s ease-out, transform 0.4s ease-out',

      padding: !hasLabel ? '6px 12px' : '6px 16px',
      fontSize: '14px',
      borderRadius: '8px',
      svg: {
        width: '20px',
        height: '20px',
      },
      '& .MuiTab-iconWrapper': {
        margin: '0px',
      },
      '&:hover': {
        color: theme.palette.case.neutral.n800,
        svg: { path: { fill: theme.palette.case.neutral.n800 } },
      },

      '&:not(:first-of-type)::before': {
        display: isLastTabOnTheRight ? 'none' : 'block',
        content: "''",
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        bottom: 0,
        left: 0,
        height: '12px',
        width: '1px',
        backgroundColor: theme.palette.case.neutral.n100,
      },
      '&.Mui-disabled': {
        color: theme.palette.case.neutral.n400,
        svg: { path: { fill: theme.palette.case.neutral.n400 } },
        pointerEvents: 'none',
      },

      '&.Mui-selected': {
        boxShadow: theme.palette.case.shadow.default,
        svg: { path: { fill: theme.palette.case.neutral.n0 } },
        color: theme.palette.case.neutral.n0,
        backgroundColor: theme.palette.case.neutral.n800,
        borderRadius: '8px',

        '& .MuiBadge-badge': {
          color: theme.palette.case.neutral.n800,
          backgroundColor: theme.palette.case.neutral.n200,
        },
        '&.Mui-disabled': {
          color: theme.palette.case.neutral.n400,
          svg: { path: { fill: theme.palette.case.neutral.n400 } },
          pointerEvents: 'none',
        },
        '&:before': {
          content: "''",
          display: 'none',
        },
      },
      ...(isLastTabOnTheRight && { marginLeft: 'auto !important' }),
      [theme.breakpoints.down('sm')]: {
        padding: '6px 12px',
      },
    }),

    ...(tabVariant === 'gray' && {
      padding: '4px 8px',
      borderRadius: '6px',
      color: theme.palette.case.neutral.n600,
      svg: {
        width: '18px',
        height: '18px',
      },

      '&:not(:first-of-type)::before': {
        display: isLastTabOnTheRight ? 'none' : 'block',
        content: "''",
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        bottom: 0,
        left: 0,
        height: '12px',
        width: '1px',
        backgroundColor: theme.palette.case.neutral.n100,
      },
      '&.Mui-disabled': {
        color: theme.palette.case.neutral.n400,
        svg: { path: { fill: theme.palette.case.neutral.n400 } },
        pointerEvents: 'none',
      },

      '&.Mui-selected': {
        boxShadow: theme.palette.case.shadow.medium,

        svg: { path: { fill: theme.palette.case.neutral.n800 } },
        color: theme.palette.case.neutral.n800,
        backgroundColor: theme.palette.case.neutral.n200,
        borderRadius: '6px',

        '& .MuiBadge-badge': {
          color: theme.palette.case.neutral.n800,
          backgroundColor: theme.palette.case.neutral.n100,
        },
        '&.Mui-disabled': {
          color: theme.palette.case.neutral.n400,
          svg: { path: { fill: theme.palette.case.neutral.n400 } },
          pointerEvents: 'none',
        },
        '&:before': {
          content: "''",
          display: 'none',
        },
      },
      [theme.breakpoints.down('sm')]: {
        '&:not(:first-of-type)::before': {
          content: "''",
          display: 'none',
        },
      },
    }),
  }),
);

type TabsProps = {
  hideTabsLabel?: boolean;
  tabVariant: TabVariant;
  minWidthForScrollable?: string;
  isHideBorderBottom?: boolean;
  tabsScrollerCustomStyles?: {};
  isSmallSize?: boolean;
};
export const TabsContainer = styled(Tabs, {
  shouldForwardProp: (prop: string) =>
    ![
      'hideTabsLabel',
      'tabVariant',
      'minWidthForScrollable',
      'isHideBorderBottom',
      'tabsScrollerCustomStyles',
      'isSmallSize',
    ].includes(prop),
})<TabsProps>(
  ({
    theme,
    hideTabsLabel,
    tabVariant,
    minWidthForScrollable,
    isHideBorderBottom,
    tabsScrollerCustomStyles,
    isSmallSize,
  }) => ({
    minHeight: isSmallSize ? '24px' : '40px',
    maxHeight: 'max-content',

    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-between',
    },

    ...(hideTabsLabel && {
      '& .MuiTabs-flexContainer': {
        display: 'flex',
        justifyContent: 'space-between',
      },
    }),

    ...(tabVariant === 'green' && {
      '& .MuiTabs-flexContainer': {
        borderBottom: isHideBorderBottom ? 'none' : `1px solid ${theme.palette.case.neutral.n100}`,
        gap: !hideTabsLabel ? '24px' : '0px',
      },
      '& .MuiTabs-indicator': {
        background: theme.palette.case.aquamarine.a700,
        height: '1px',
        bottom: '0px',
      },
    }),

    ...(tabVariant === 'dark' && {
      '& .MuiTabs-flexContainer': {
        padding: '4px',
        gap: '0px',
        backgroundColor: theme.palette.case.neutral.n0,
        [theme.breakpoints.down('sm')]: {
          minWidth: minWidthForScrollable || '365px',
          justifyContent: 'initial',
        },
      },
      '& .MuiTabs-indicator': { visibility: 'hidden' },

      '& .MuiTabs-scroller': {
        border: `1px solid ${theme.palette.case.neutral.n200}`,
        borderRadius: '8px',
        backgroundColor: theme.palette.case.neutral.n0,
      },
    }),

    ...(tabVariant === 'gray' && {
      '& .MuiTabs-flexContainer': {
        padding: '2px',
        gap: '0px',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.case.neutral.n0,
      },
      '& .MuiTabs-indicator': { visibility: 'hidden' },

      '& .MuiTabs-scroller': {
        border: `1px solid ${theme.palette.case.neutral.n200}`,
        borderRadius: '7px',
        ...tabsScrollerCustomStyles,
      },
    }),
  }),
);

export const NavigationContainer = styled(Box)(({ theme }) => ({
  maxHeight: '100%',
  width: '100%',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginBottom: '16px',
  },
}));
